import { UserPicker, MXID_RE } from "../components/UserPicker";
import { ActionStatus } from "../components/ActionStatus";
import { useDialog } from "../components/DialogProvider";
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { hasGroup, useAuth } from "../auth/AuthContext";
import {
  deleteRecording,
  deleteSummary,
  getRecording,
  getRecordingUrls,
  getSummaries,
  listUsers,
  patchRecording,
  resummarize,
  setRecordingVisibility,
  retranscribe,
  updateSummary,
} from "../api/recordings";
import { ApiError } from "../api/client";
import { createTag, linkTag, listTags } from "../api/tags";
import type { AutocompleteUser, PatchPayload } from "../api/recordings";
import type {
  Recording,
  RecordingVisibility,
  RecordingTag,
  SummaryRow,
  TranscriptJSON,
  UrlsResponse,
} from "../types";
import { summaryProfileLabel, useSummaryProfiles } from "../summaryProfiles";

/** Имя admin-группы в Authentik (AUTHENTIK_ADMIN_GROUP). admin видит/правит всё
 *  и может переназначать владельца записи. Должно совпадать с .env recorder-backend. */
const ADMIN_GROUP = "recordings-admin";

/** Расширения, которые HTML5 <video>/<audio> играют надёжно во всех бровзерах. */
const PLAYABLE_EXTS = new Set([
  "mp4", "webm", "m4v", "mp3", "m4a", "wav", "ogg", "opus", "flac",
]);

/**
 * isPlayableInBrowser возвращает true если запись можно проиграть нативно в
 * <video>/<audio>. Для звонков (final.webm) — всегда true. Для upload'ов
 * проверяем расширение оригинального файла по whitelist'у. MOV/MKV/AVI —
 * false (Chrome/FF не играют, Safari иногда).
 */
function isPlayableInBrowser(rec: Recording | null): boolean {
  if (!rec) return false;
  if (rec.source !== "upload") return true;
  const filename = rec.original_filename || "";
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  return PLAYABLE_EXTS.has(ext);
}

/** Для upload'ов human-readable расширение для метки download'а. */
function downloadLabelForMedia(rec: Recording | null): string {
  if (!rec || rec.source !== "upload") return "Скачать видео (.webm)";
  const filename = rec.original_filename || "";
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  return `Скачать оригинал (.${ext || "bin"})`;
}

export default function RecordingView() {
  const { confirm, alert } = useDialog();
  const [deletingRecording, setDeletingRecording] = useState(false);
  const deletingRecordingRef = useRef(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Глобальная роль admin (для переназначения владельца). Право управления
  // конкретной записью считает бэк (recording.can_manage) с учётом владельца.
  const isAdmin = hasGroup(user, ADMIN_GROUP);

  // Initial seek по ?ms=N в URL — используется при переходе из поиска (клик
  // по сниппету). Видео seek'ается на нужную секунду, но НЕ запускается —
  // пользователь сам решит проигрывать. transcript-панель auto-scroll'ится
  // на этот сегмент через currentTime → findActiveSegment (см. TranscriptPanel).
  //
  // ?q= тоже принимаем для forward-совместимости (пока не используем, но
  // можем в будущем подсвечивать в полном транскрипте).
  const [searchParams] = useSearchParams();
  const initialSeekSec = useMemo(() => {
    const raw = searchParams.get("ms");
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) return null;
    return n / 1000;
  }, [searchParams]);

  // URL для "← К списку" — сохраняем filter-params списка (q/room/source/
  // from/to/page), убираем только recording-specific (ms). Чтобы юзер
  // вернулся на ту же страницу поиска что и был. Обычная <Link to="/">
  // дропает search-часть → весь поиск сбрасывается.
  const backToListUrl = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("ms");
    const qs = p.toString();
    return qs ? `/?${qs}` : "/";
  }, [searchParams]);

  const [recording, setRecording] = useState<Recording | null>(null);
  const [urls, setUrls] = useState<UrlsResponse | null>(null);
  const [transcript, setTranscript] = useState<TranscriptJSON | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // currentTime инициализируем тем же значением что и initialSeek (если есть),
  // чтобы TranscriptPanel сразу подсветил правильный сегмент ещё ДО того как
  // <video> успеет emit'нуть первый timeupdate — иначе была бы видна доля
  // секунды с подсветкой нулевого сегмента.
  const [currentTime, setCurrentTime] = useState(initialSeekSec ?? 0);
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [videoHeight, setVideoHeight] = useState<number>(0);
  // seek-once flag: после первой успешной установки video.currentTime больше
  // не трогаем (не хотим прерывать пользовательскую перемотку при ре-рендерах).
  const initialSeekDoneRef = useRef(false);

  useEffect(() => {
    if (!id) return;
    // Параллельно загружаем метаданные и pre-signed URLs.
    Promise.all([getRecording(id), getRecordingUrls(id)])
      .then(([rec, u]) => {
        setRecording(rec);
        setUrls(u);
        const transcriptJsonURL = u.transcript_json_url;
        if (transcriptJsonURL) {
          fetch(transcriptJsonURL)
            .then((res) => (res.ok ? res.json() : null))
            .then((data: TranscriptJSON | null) => setTranscript(data))
            .catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          setError("У вас нет доступа к этой записи.");
        } else if (err instanceof ApiError && err.status === 404) {
          setError("Запись не найдена.");
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
  }, [id]);

  const handleDelete = async () => {
    if (!id || deletingRecordingRef.current) return;
    deletingRecordingRef.current = true;
    const ok = await confirm({ title: "Удалить запись?", description: "Видео и транскрипт будут удалены безвозвратно.", confirmLabel: "Удалить", danger: true });
    if (!ok) { deletingRecordingRef.current = false; return; }
    setDeletingRecording(true);
    try {
      await deleteRecording(id);
      // Возврат на список с сохранёнными filter-params (тот же URL что у "← К списку").
      navigate(backToListUrl, { replace: true });
    } catch (err) {
      // 401 = session expired, apiFetch уже инициировал reauth. Не отвлекаем
      // юзера alert'ом — он сейчас уйдёт на login и вернётся.
      if (err instanceof ApiError && err.status === 401) return;
      await alert({ title: "Не удалось удалить запись", description: err instanceof Error ? err.message : String(err) });
    } finally {
      deletingRecordingRef.current = false;
      setDeletingRecording(false);
    }
  };

  // Ретрай транскрибации упавшей записи. Бросает ошибку наружу — FailedPanel
  // её ловит и показывает. При успехе обновляем запись (status → assembled),
  // панель ошибки исчезает сама.
  const handleRetry = async (diarize: boolean) => {
    if (!id) return;
    const updated = await retranscribe(id, diarize);
    setRecording(updated);
  };

  // Переименование записи (manager) — обновляем локально из ответа PATCH.
  const handleRename = useCallback(
    async (title: string) => {
      if (!id) return;
      const updated = await patchRecording(id, { title });
      setRecording(updated);
    },
    [id],
  );

  const videoSrc = urls?.video_url;
  const transcriptTxt = urls?.transcript_txt_url;
  const transcriptJSON = urls?.transcript_json_url;

  // hasPlayer — рисуем ли мы реальный <video>. От этого зависит, к чему
  // прибивать высоту transcript-панели (см. useLayoutEffect ниже).
  const hasPlayer = Boolean(videoSrc) && isPlayableInBrowser(recording);

  // Высота transcript-side прибивается к высоте <video> через React state.
  // CSS Grid сам не справляется: min-height: 0 + overflow: hidden не подавляют
  // intrinsic size grid item'ов с большим внутренним контентом. Меряем через
  // ResizeObserver и применяем как style={{ height }} в JSX.
  //
  // ВАЖНО: якорь есть только когда плеер реально отрисован. Для форматов, что
  // браузер не играет (MOV/MKV/AVI), и для записей без готового медиа в левой
  // колонке лежит маленькая заглушка, videoRef.current === null, эффект выходит
  // сразу — и без запасного варианта панель растягивалась на всю длину
  // транскрипта (страница на несколько экранов при 200px-заглушке слева).
  // Прибивать высоту к самой заглушке тоже нельзя: 180px под транскрипт —
  // издевательство. Поэтому в этом случае inline-высоту не ставим вовсе,
  // а размер задаёт CSS-модификатор --no-video.
  useLayoutEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const apply = (): void => setVideoHeight(v.offsetHeight);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(v);
    return () => ro.disconnect();
  }, [transcriptOpen, videoSrc, hasPlayer]);

  if (error) {
    return (
      <div className="page">
        <p>
          <Link to={backToListUrl}>← К списку</Link>
        </p>
        <h1>Запись</h1>
        <p className="error">{error}</p>
      </div>
    );
  }

  if (!urls) {
    return (
      <div className="page">
        <p>
          <Link to={backToListUrl}>← К списку</Link>
        </p>
        <p className="muted">Загрузка…</p>
      </div>
    );
  }

  const handleSegmentClick = (start: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = start;
      videoRef.current.play().catch(() => undefined);
    }
  };

  const fallbackTitle = recording?.room_display_name || recording?.room_id || "Запись";
  const displayedTitle = recording?.title?.trim() || fallbackTitle;
  // Право управления ЭТОЙ записью считает бэк (admin → всё; manager → своё;
  // viewer → нет). UI рисует кнопки по нему.
  const canManage = recording?.can_manage ?? false;

  return (
    <div className="page">
      <p>
        <Link to={backToListUrl}>← К списку</Link>
      </p>
      <RecordingTitle
        title={displayedTitle}
        canEdit={canManage}
        onRename={handleRename}
      />
      {recording && <RecordingMeta rec={recording} />}
      {recording && !canManage && <VisibilityPanel rec={recording} canManage={false} canConfidential={false} onChanged={setRecording} />}
      {recording && !canManage && <TagPanel rec={recording} canManage={false} onChanged={setRecording} />}
      {/* Упавшая транскрибация: причину видят все, кто видит запись; кнопка
        * перезапуска — только тем, кто может управлять (admin / владелец). */}
      {recording?.status === "failed" && (
        <FailedPanel rec={recording} onRetry={handleRetry} canRetry={canManage} />
      )}
      {/* Кто не может управлять — read-only список доступа; управляющий
        * редактирует его в ManagerPanel ниже. */}
      {recording && !canManage && <AccessList rec={recording} />}

      {/* Toolbar над панелями: единственный toggle транскрипта.
        * По умолчанию open. */}
      {transcript && transcript.segments.length > 0 && (
        <div className="recording-toolbar">
          <button
            type="button"
            className="page-button transcript-toggle"
            onClick={() => setTranscriptOpen((o) => !o)}
            aria-pressed={transcriptOpen}
          >
            ☰ Транскрипт
          </button>
        </div>
      )}

      {/* Пометка для записей, транскрибированных без диаризации (ретрай в
        * лёгком режиме или диаризация не отработала): у сегментов нет
        * speaker-меток — поясняем, что сплошной текст это не баг. */}
      {transcript?.diarized === false && transcript.segments.length > 0 && (
        <p className="transcript-mode-note muted">
          ℹ Транскрибировано без разделения по говорящим — текст без меток «кто
          говорит».
        </p>
      )}

      {/* Grid: 1fr | 360px. Высота row = высота видео (aspect 16:9).
        * Transcript-list внутри transcript-side имеет overflow: auto,
        * поэтому скроллится сам по себе, не дёргая страницу. */}
      <div className={`recording-pane ${transcriptOpen ? "" : "recording-pane--solo"}`}>
        <div className="recording-video">
          {hasPlayer ? (
            <video
              ref={videoRef}
              controls
              src={videoSrc}
              onLoadedMetadata={(e) => {
                // Seek по ?ms= — однократно, при первой готовности.
                // Делать seek до loadedmetadata бесполезно: до этого момента
                // duration ≈ NaN и .currentTime игнорируется браузером.
                if (initialSeekDoneRef.current || initialSeekSec === null) return;
                const v = e.currentTarget;
                const d = v.duration;
                // Clamp на длительность — если ?ms= оказался > длины
                // (например, ссылка из другой версии записи) садимся в конец,
                // не за границей.
                const target = Number.isFinite(d) && d > 0
                  ? Math.min(initialSeekSec, Math.max(0, d - 0.01))
                  : initialSeekSec;
                v.currentTime = target;
                setCurrentTime(target);
                initialSeekDoneRef.current = true;
                // НЕ вызываем v.play() — переход остаётся на паузе по UX.
              }}
              onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
            />
          ) : videoSrc ? (
            // Upload в формате, который браузер не играет нативно (MOV/MKV/AVI).
            // Показываем download-CTA вместо плеера; transcript остаётся справа.
            <div className="muted unplayable-note">
              <p>Формат файла не воспроизводится в браузере.</p>
              <p>Скачайте оригинал и откройте в локальном плеере.</p>
              <a
                href={
                  videoSrc + (videoSrc.includes("?") ? "&" : "?") + "download=1"
                }
                download=""
                rel="noreferrer"
                className="page-button primary"
              >
                ⬇ {downloadLabelForMedia(recording)}
              </a>
            </div>
          ) : (
            // videoSrc нет вообще — запись ещё не дошла до стадии, где медиа
            // готово (recording / uploading / finalized).
            <p className="muted">
              {recording?.status === "recoverable"
                ? "Ожидаем докачки с устройства, на котором велась запись. Откройте там NAN Chat или браузер и список локальных записей."
                : "Видео ещё не готово (запись в обработке). Попробуй обновить страницу через минуту."}
            </p>
          )}
        </div>
        {transcript && transcript.segments.length > 0 && transcriptOpen && (
          <div
            className={
              "recording-transcript-side" +
              (hasPlayer ? "" : " recording-transcript-side--no-video")
            }
            style={
              hasPlayer && videoHeight > 0 ? { height: videoHeight } : undefined
            }
          >
            <TranscriptPanel
              segments={transcript.segments}
              currentTime={currentTime}
              onClick={handleSegmentClick}
            />
          </div>
        )}
      </div>

      {/* Ссылки на скачивание — сразу под видео, над конспектом. */}
      <DownloadLinks
        videoURL={videoSrc}
        videoLabel={downloadLabelForMedia(recording)}
        txtURL={transcriptTxt}
        jsonURL={transcriptJSON}
      />

      {recording && (
        <SummarySection recording={recording} canManage={canManage} />
      )}

      {canManage && recording && (
        <ManagerPanel
          recording={recording}
          isAdmin={isAdmin}
          canConfidential={isAdmin || hasGroup(user, "recordings-supermanager")}
          onChanged={setRecording}
          onSave={async (payload) => {
            if (!id) return;
            const updated = await patchRecording(id, payload);
            setRecording(updated);
          }}
          onDelete={handleDelete}
          deleting={deletingRecording}
        />
      )}
    </div>
  );
}

function VisibilityPanel({rec, canManage, canConfidential, onChanged}: {rec: Recording; canManage: boolean; canConfidential: boolean; onChanged: (r: Recording) => void}) {
  const { confirm } = useDialog();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const changing = useRef(false);
  const navigate=useNavigate();
  const {user}=useAuth();
  const current = rec.visibility || "restricted";
  const label = current === "staff" ? "Всем сотрудникам NAN" : current === "confidential" ? "Конфиденциальная" : "Ограниченный доступ";
  async function change(next: RecordingVisibility) {
    if (next === current || changing.current) return;
    changing.current = true;
    setSaving(true); setStatus("Проверяем доступ…"); setError("");
    let saved = false;
    try {
      const fresh=await getRecording(rec.id);
      if (fresh.visibility !== current) throw new Error("Режим записи изменился. Обновите страницу.");
      const allTags=await listTags();
      const tagRecipients=(fresh.tags ?? []).filter(t=>t.grants_access).map(t=>{const tag=allTags.find(x=>x.id===t.id);return `${t.name}: ${(tag?.grants ?? []).map(g=>g.principal).join(", ") || "нет получателей"}`;}).join("; ") || "нет";
      const names = fresh.acl_users?.length ? fresh.acl_users.join(", ") : "нет";
      const warning = next === "staff" ? "Запись увидят все сотрудники NAN с доступом к порталу." : next === "restricted" ? "Запись увидят все суперменеджеры." : "Запись увидят владелец и получатели с доступом.";
      const self=user?.profile.matrix_id;
      const hasExplicit=fresh.started_by===self || !!fresh.acl_users?.includes(self ?? "") || (fresh.tags ?? []).some(t=>t.grants_access && allTags.find(x=>x.id===t.id)?.grants?.some(g=>g.type==="user" ? g.principal===self : user?.profile.groups.includes(g.principal)));
      const loseAccess=next==="confidential" && !hasExplicit && !hasGroup(user, "recordings-admin");
      setStatus("");
      if (!await confirm({ title: "Изменить доступ?", description: `${warning}\nПерсональный доступ: ${names}.\nДоступ через метки: ${tagRecipients}.${loseAccess ? "\nВы потеряете доступ к этой записи." : ""}`, confirmLabel: "Изменить доступ" })) return;
      setStatus("Сохраняем доступ…");
      await setRecordingVisibility(rec.id, current, next, fresh.updated_at, Object.fromEntries((fresh.tags ?? []).map(t=>[t.id,t.version])));
      saved = true;
      if (loseAccess) { navigate("/"); return; }
      onChanged(await getRecording(rec.id));
    }
    catch (e) { setError(saved ? "Доступ изменён, но не удалось обновить запись. Обновите страницу." : e instanceof Error ? e.message : "Не удалось изменить доступ"); }
    finally { changing.current = false; setSaving(false); setStatus(""); }
  }
  return <section className="access-list"><h3>Доступ: {label}</h3>
    {canManage && <><label htmlFor="recording-visibility">Изменить режим</label>{" "}<select className="filter-select" id="recording-visibility" value={current} disabled={saving || ["recording", "recoverable", "uploading"].includes(rec.status)} onChange={e => void change(e.target.value as RecordingVisibility)}>
      <option value="restricted">Ограниченный доступ</option><option value="staff">Всем сотрудникам NAN</option>{(canConfidential || current === "confidential") && <option value="confidential">Конфиденциальная</option>}
    </select></>}
    {status && <p className="action-status-row"><ActionStatus>{status}</ActionStatus></p>}
    {error && <p className="error">{error}</p>}
  </section>;
}

function TagPanel({ rec, canManage, onChanged }: { rec: Recording; canManage: boolean; onChanged: (r: Recording) => void }) {
  const [available, setAvailable] = useState<RecordingTag[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<{ action: "add" | "remove"; tag: RecordingTag } | null>(null);
  const [busy, setBusy] = useState("");
  const busyRef = useRef(false);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsFailed, setTagsFailed] = useState(false);
  const [error, setError] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const refresh = useCallback(async () => { setAvailable(await listTags()); onChanged(await getRecording(rec.id)); }, [onChanged, rec.id]);
  useEffect(() => { if (canManage) { setTagsLoading(true); setTagsFailed(false); void listTags().then(setAvailable).catch(() => { setTagsFailed(true); setError("Не удалось загрузить метки. Обновите страницу."); }).finally(() => setTagsLoading(false)); } else setTagsLoading(false); }, [canManage, rec.id]);
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const outside = (e: PointerEvent) => { if (!pickerRef.current?.contains(e.target as Node)) setOpen(false); };
    const escape = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("pointerdown", outside); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  const attached = new Set((rec.tags ?? []).map(t => t.id));
  const search = query.trim().toLocaleLowerCase();
  const choices = available.filter(t => !t.archived && !attached.has(t.id) && t.name.toLocaleLowerCase().includes(search));
  const exact = available.some(t => t.name.toLocaleLowerCase() === search);
  async function change(tag: RecordingTag, action: "add" | "remove") {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(action === "add" ? "Добавляем метку…" : "Убираем метку…"); setError("");
    let changed = false;
    try { await linkTag(rec.id, tag.id, action === "add", action === "add" ? tag.version : undefined); changed = true; setPending(null); setOpen(false); setQuery(""); await refresh(); }
    catch { setError(changed ? "Метка изменена, но не удалось обновить запись. Обновите страницу." : action === "add" ? "Не удалось добавить метку. Обновите страницу и проверьте получателей." : "Не удалось убрать метку."); }
    finally { busyRef.current = false; setBusy(""); }
  }
  async function create() {
    if (!query.trim() || exact || busyRef.current) return;
    busyRef.current = true; setBusy("Создаём метку…"); setError("");
    let created = false; let linked = false;
    try { const tag = await createTag(query.trim()); created = true; setBusy("Добавляем метку…"); await linkTag(rec.id, tag.id, true, tag.version); linked = true; setOpen(false); setQuery(""); await refresh(); }
    catch { setError(linked ? "Метка добавлена, но не удалось обновить запись. Обновите страницу." : created ? "Метка создана, но не добавлена к записи. Обновите страницу и добавьте её из списка." : "Не удалось создать метку. Попробуйте снова."); }
    finally { busyRef.current = false; setBusy(""); }
  }
  function choose(tag: RecordingTag, action: "add" | "remove") {
    setOpen(false); setError("");
    if (tag.grants_access) setPending({ action, tag: available.find(t => t.id === tag.id) ?? tag });
    else void change(tag, action);
  }
  return <section className="access-list recording-tags">
    <div className="recording-tags-heading"><div><h3>Метки</h3><p className="tag-caption">Помогают найти запись и управлять доступом</p></div>
      {canManage && <div className="tag-picker" ref={pickerRef}><button ref={triggerRef} type="button" className="tag-add-button" aria-expanded={open} aria-controls="recording-tag-picker" disabled={!!busy || tagsLoading || tagsFailed} onClick={() => { setPending(null); setOpen(!open); }}><span aria-hidden="true">＋</span> Добавить метку</button>
        {open && <div className="tag-popover" id="recording-tag-picker" role="dialog" aria-label="Добавить метку">
          <input ref={inputRef} className="tag-search" type="search" placeholder="Найти или создать метку" aria-label="Поиск меток" value={query} disabled={!!busy} onChange={e => setQuery(e.target.value)} maxLength={150} />
          <div className="tag-options">{choices.map(tag => <button className="tag-option" type="button" key={tag.id} disabled={!!busy} onClick={() => choose(tag, "add")}><span className="tag-dot" style={tag.color ? { background: tag.color } : undefined} /><span className="tag-option-name">{tag.name}</span>{tag.grants_access && <span className="tag-option-access">С доступом</span>}</button>)}
          {search && !exact && <button className="tag-option tag-create" type="button" disabled={!!busy} onClick={() => void create()}><span aria-hidden="true">＋</span><span>Создать «{query.trim()}»<small>Только для поиска, без выдачи доступа</small></span></button>}
          {!choices.length && (!search || exact) && <p className="tag-empty">{search ? "Метка уже добавлена или находится в архиве" : "Нет доступных меток. Введите название, чтобы создать."}</p>}</div>
        </div>}
      </div>}
    </div>
    {(rec.tags ?? []).length ? <div className="recording-tag-chips">{(rec.tags ?? []).map(tag => <span className="recording-tag-chip" key={tag.id}><span className="tag-dot" style={tag.color ? { background: tag.color } : undefined} />{tag.name}{tag.grants_access && <span className="tag-chip-access" title="Метка выдаёт доступ">Доступ</span>}{canManage && <button type="button" aria-label={`Убрать ${tag.name}`} disabled={!!busy} onClick={() => choose(tag, "remove")}>×</button>}</span>)}</div> : <p className="tag-empty-current">Метки пока не добавлены</p>}
    {canManage && (rec.tags ?? []).filter(tag => tag.grants_access).map(tag => <p className="tag-recipients" key={tag.id}><strong>{tag.name}:</strong> {available.find(t => t.id === tag.id) ? (available.find(t => t.id === tag.id)?.grants?.map(g => g.principal).join(", ") || "получателей пока нет") : "загрузка получателей…"}</p>)}
    {pending && <div className="tag-confirm" role="region" aria-label="Подтверждение изменения метки"><strong>{pending.action === "add" ? `Метка «${pending.tag.name}» предоставит доступ` : `Убрать метку «${pending.tag.name}»?`}</strong><p>{pending.action === "add" ? <>Получатели: {(pending.tag.grants ?? []).map(g => g.principal).join(", ") || "пока нет получателей"}. Список получателей может измениться позднее.</> : "Получатели могут потерять доступ к записи, если у них нет другого основания."}</p><div className="tag-confirm-actions"><button type="button" className="page-button" disabled={!!busy} onClick={() => setPending(null)}>Отмена</button><button type="button" className="page-button primary" disabled={!!busy} onClick={() => void change(pending.tag, pending.action)}>{pending.action === "add" ? "Предоставить доступ" : "Убрать метку"}</button></div></div>}
    {(busy || tagsLoading) && <p className="action-status-row"><ActionStatus>{busy || "Загружаем метки…"}</ActionStatus></p>}
    {error && <p className="error" role="alert">{error}</p>}
  </section>;
}

/** Человеческий текст причины провала по коду из recording.failure_reason.
 *  Без техжаргона: пользователь по тексту решает, стоит ли перезапускать. */
function failureReasonText(code: string | undefined): string {
  switch (code) {
    case "oom":
      return (
        "Серверу не хватило памяти на обработку — запись слишком длинная или " +
        "большая. Попробуйте перезапустить в режиме «без разделения по " +
        "говорящим»: он требует заметно меньше памяти."
      );
    case "download_failed":
      return (
        "Не удалось загрузить файл записи для обработки. Обычно это временный " +
        "сбой — можно попробовать перезапустить."
      );
    case "media_missing":
      return (
        "Файл записи не найден в хранилище. Обычно это значит, что загрузка не " +
        "завершилась — например, вкладку закрыли до конца отправки файла. " +
        "Перезапуск не поможет: загрузите файл заново."
      );
    case "decode_failed":
      return (
        "Файл записи повреждён или сохранён в неподдерживаемом формате, поэтому " +
        "транскрибация невозможна. Перезапуск, скорее всего, не поможет."
      );
    case "assembly_failed":
      return (
        "Не удалось собрать файл записи из фрагментов. Можно попробовать " +
        "перезапустить."
      );
    default:
      return "При обработке записи произошла ошибка. Можно попробовать перезапустить.";
  }
}

/**
 * FailedPanel — показывается для записей в статусе 'failed'. Причину видят все,
 * кто видит запись; кнопка перезапуска — только тем, кто может управлять записью
 * (canRetry: admin или владелец-manager). Режимы перезапуска:
 *   - Полный — со спикерами (кто что сказал), лучшее качество, больше памяти.
 *   - Без разделения по говорящим — сплошной текст, заметно легче по памяти
 *     (основной способ дожать длинную запись после OOM).
 */
function FailedPanel({
  rec,
  onRetry,
  canRetry,
}: {
  rec: Recording;
  onRetry: (diarize: boolean) => Promise<void>;
  canRetry: boolean;
}) {
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const retry = async (diarize: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await onRetry(diarize);
      setChoosing(false);
    } catch (e) {
      // 401 = сессия истекла, apiFetch уже инициировал reauth — не шумим.
      if (e instanceof ApiError && e.status === 401) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="failed-panel">
      <h3 className="failed-title">⚠ Транскрибация не удалась</h3>
      <p className="failed-reason">{failureReasonText(rec.failure_reason)}</p>

      {!canRetry ? null : !choosing ? (
        <button
          type="button"
          className="page-button primary"
          disabled={busy}
          onClick={() => setChoosing(true)}
        >
          Перезапустить транскрибацию
        </button>
      ) : (
        <div className="failed-modes">
          <p className="muted modes-label">
            Выберите режим повторной обработки:
          </p>
          <button
            type="button"
            className="retry-mode-button"
            disabled={busy}
            onClick={() => retry(true)}
          >
            <strong>Полный</strong>
            <span>
              С разделением по говорящим («кто что сказал»). Лучшее качество,
              требует больше памяти.
            </span>
          </button>
          <button
            type="button"
            className="retry-mode-button"
            disabled={busy}
            onClick={() => retry(false)}
          >
            <strong>Без разделения по говорящим</strong>
            <span>
              Сплошной текст без меток «кто говорит». Заметно легче по памяти —
              выбирайте для длинных записей.
            </span>
          </button>
          <button
            type="button"
            className="link-button"
            disabled={busy}
            onClick={() => setChoosing(false)}
          >
            Отмена
          </button>
        </div>
      )}

      {busy && <p className="muted">Ставим запись в очередь…</p>}
      {err && <p className="error">{err}</p>}
    </section>
  );
}

/**
 * SummarySection — конспект записи с вкладками по стилям (sales/tech/meetup/
 * interview). Все стили равноправны; вкладка, выбранная при загрузке, открывается
 * первой. Понятия «основной конспект» нет — переключение вкладок ничего не пишет,
 * перегенерация меняет только свою вкладку. Тянет варианты через GET …/summaries;
 * пока что-то генерируется (pending/running) — поллит раз в 5с.
 *
 * Перегенерация (↻ Другой тип) доступна всем, кто видит запись (бэк проверяет
 * доступ), только для записей с готовым транскриптом (status='transcribed').
 * Ручная правка текста вкладки — только manager (isManager).
 *
 * Legacy-фоллбэк: у старых записей строк recording_summaries нет, но есть
 * recording.summary — показываем его одной вкладкой.
 */
function SummarySection({
  recording,
  canManage,
}: {
  recording: Recording;
  /** Может ли текущий пользователь править/перегенерировать конспект этой
   *  записи (admin или владелец-manager). Гейтит «Другой тип» и «Изменить». */
  canManage: boolean;
}) {
  const { confirm } = useDialog();
  const id = recording.id;
  const [summaries, setSummaries] = useState<SummaryRow[] | null>(null);
  const [activeProfile, setActiveProfile] = useState<string>(
    recording.summary_profile ?? "",
  );
  const [selected, setSelected] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [busyProfile, setBusyProfile] = useState<string | null>(null);
  // Ручное редактирование вкладки (manager): профиль в режиме правки + черновик.
  const [editingProfile, setEditingProfile] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Профиль, который сейчас удаляется (блокирует кнопки на время запроса).
  const [deletingProfile, setDeletingProfile] = useState<string | null>(null);
  // Контейнер дропдауна «Другой тип» — для закрытия по клику вне его.
  const regenRef = useRef<HTMLDivElement | null>(null);
  // Список профилей с бэка (для меню «Другой тип»). Хук на верхнем уровне —
  // ДО раннего return ниже (правила хуков).
  const { profiles: allProfiles } = useSummaryProfiles();

  // Закрываем дропдаун «Другой тип» при клике вне него и по Escape.
  useEffect(() => {
    if (!regenOpen) return;
    const onDocClick = (e: Event) => {
      if (regenRef.current && !regenRef.current.contains(e.target as Node)) {
        setRegenOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setRegenOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [regenOpen]);

  useEffect(() => {
    let cancelled = false;
    getSummaries(id)
      .then((res) => {
        if (cancelled) return;
        setSummaries(res.summaries);
        setActiveProfile(res.active_profile);
      })
      .catch(() => {
        if (!cancelled) setSummaries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Legacy-фоллбэк: нет строк, но есть recording.summary.
  const effective: SummaryRow[] = useMemo(() => {
    if (summaries && summaries.length > 0) return summaries;
    if (recording.summary?.trim()) {
      return [
        {
          profile: recording.summary_profile || activeProfile || "sales",
          title: recording.title,
          summary: recording.summary,
          status: "ready",
          created_at: recording.created_at,
          updated_at: recording.updated_at,
        },
      ];
    }
    return [];
  }, [
    summaries,
    activeProfile,
    recording.summary,
    recording.summary_profile,
    recording.title,
    recording.created_at,
    recording.updated_at,
  ]);

  // Поллинг пока есть незавершённые варианты.
  const hasPending = (summaries ?? []).some(
    (s) => s.status === "pending" || s.status === "running",
  );
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => {
      getSummaries(id)
        .then((res) => {
          setSummaries(res.summaries);
          setActiveProfile(res.active_profile);
        })
        .catch(() => undefined);
    }, 5000);
    return () => clearInterval(t);
  }, [hasPending, id]);

  // Держим выбранный таб валидным.
  useEffect(() => {
    if (effective.length === 0) return;
    if (!effective.some((s) => s.profile === selected)) {
      const fallback =
        activeProfile && effective.some((s) => s.profile === activeProfile)
          ? activeProfile
          : effective[0].profile;
      setSelected(fallback);
    }
  }, [effective, selected, activeProfile]);

  const canRegen = recording.status === "transcribed";

  const handleRegen = async (p: string) => {
    setBusyProfile(p);
    setError(null);
    setRegenOpen(false);
    setEditingProfile(null);
    try {
      const res = await resummarize(id, p);
      setSummaries(res.summaries);
      setActiveProfile(res.active_profile);
      setSelected(p);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyProfile(null);
    }
  };

  const selectTab = (p: string) => {
    setSelected(p);
    setEditingProfile(null); // переключение вкладки сбрасывает несохранённую правку
  };

  const startEdit = (row: SummaryRow) => {
    setEditingProfile(row.profile);
    setDraft(row.summary ?? "");
    setError(null);
  };

  const saveEdit = async (profile: string) => {
    setSavingEdit(true);
    setError(null);
    try {
      const res = await updateSummary(id, profile, draft.trim());
      setSummaries(res.summaries);
      setActiveProfile(res.active_profile);
      setEditingProfile(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (profile: string) => {
    if (
      !await confirm({ title: "Удалить конспект?", description: `Конспект «${summaryProfileLabel(profile)}» будет удалён безвозвратно.`, confirmLabel: "Удалить", danger: true })
    ) {
      return;
    }
    setDeletingProfile(profile);
    setError(null);
    setEditingProfile(null);
    try {
      const res = await deleteSummary(id, profile);
      setSummaries(res.summaries);
      setActiveProfile(res.active_profile);
      setSelected(""); // эффект пере-выберет валидный таб
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingProfile(null);
    }
  };

  // Нечего показать (нет вариантов) и нельзя сгенерировать → секции нет.
  if (effective.length === 0 && !(canRegen && canManage)) return null;

  const current =
    effective.find((s) => s.profile === selected) ?? effective[0];
  const editing = current != null && editingProfile === current.profile;
  const canEditCurrent =
    canManage && current != null && current.status === "ready" && !editing;

  // Для меню «Другой тип»: профили, которых ещё нет (создать) и уже есть
  // (перегенерировать) — показываем двумя группами, чтобы было понятно действие.
  const ungeneratedProfiles = allProfiles.filter(
    (p) => !effective.some((s) => s.profile === p.slug),
  );
  const generatedProfiles = allProfiles.filter((p) =>
    effective.some((s) => s.profile === p.slug),
  );

  return (
    <section className="summary-view">
      <div className="summary-head">
        <div className="summary-head-left">
          <h2>Конспект</h2>
          {current && canEditCurrent && (
            <button
              type="button"
              className="link-button"
              onClick={() => startEdit(current)}
            >
              ✎ Изменить
            </button>
          )}
        </div>
        {canRegen && canManage && (
          <div className="summary-regen" ref={regenRef}>
            <button
              type="button"
              className="link-button"
              onClick={() => setRegenOpen((o) => !o)}
              disabled={busyProfile !== null}
            >
              ↻ Другой тип
            </button>
            {regenOpen && (
              <ul className="summary-regen-menu">
                {ungeneratedProfiles.length > 0 && (
                  <li className="summary-regen-header">Создать конспект</li>
                )}
                {ungeneratedProfiles.map((p) => (
                  <li key={p.slug}>
                    <button
                      type="button"
                      className="summary-regen-item"
                      onClick={() => handleRegen(p.slug)}
                    >
                      <span className="summary-regen-icon">＋</span>
                      {p.label}
                    </button>
                  </li>
                ))}
                {generatedProfiles.length > 0 && (
                  <li className="summary-regen-header">Перегенерировать</li>
                )}
                {generatedProfiles.map((p) => (
                  <li key={p.slug}>
                    <button
                      type="button"
                      className="summary-regen-item"
                      onClick={() => handleRegen(p.slug)}
                    >
                      <span className="summary-regen-icon">↻</span>
                      {p.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {effective.length > 1 && (
        <div className="summary-tabs">
          {effective.map((s) => {
            const generating =
              s.status === "pending" || s.status === "running";
            // Крестик только у того, кто может управлять, для реальной строки
            // recording_summaries (не legacy recording.summary) и не пока она
            // генерится. Одиночный конспект сюда не попадает (нет ряда табов) —
            // его таб остаётся, удалять нечем.
            const showDel =
              canManage &&
              !generating &&
              (summaries?.some((x) => x.profile === s.profile) ?? false);
            return (
              <span
                key={s.profile}
                className={
                  "summary-tab" +
                  (s.profile === selected ? " summary-tab--active" : "")
                }
              >
                <button
                  type="button"
                  className="summary-tab-label"
                  onClick={() => selectTab(s.profile)}
                >
                  {summaryProfileLabel(s.profile)}
                  {generating && " …"}
                </button>
                {showDel && (
                  <button
                    type="button"
                    className="summary-tab-del"
                    title="Удалить конспект"
                    aria-label={`Удалить конспект «${summaryProfileLabel(
                      s.profile,
                    )}»`}
                    disabled={deletingProfile !== null}
                    onClick={() => handleDelete(s.profile)}
                  >
                    ✕
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {effective.length === 1 && (
        <p className="summary-single-type muted">
          Тип: {summaryProfileLabel(current?.profile ?? "")}
        </p>
      )}

      {busyProfile && <p className="action-status-row"><ActionStatus>Запускаем создание конспекта…</ActionStatus></p>}
      {deletingProfile && <p className="action-status-row"><ActionStatus>Удаляем конспект…</ActionStatus></p>}
      {error && <p className="error">{error}</p>}

      {editing && current ? (
        <div className="summary-edit">
          <textarea
            className="manager-textarea"
            rows={14}
            maxLength={100000}
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="manager-row row-end">
            <button
              type="button"
              className="link-button"
              disabled={savingEdit}
              onClick={() => setEditingProfile(null)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="page-button"
              disabled={savingEdit || draft.trim() === ""}
              onClick={() => saveEdit(current.profile)}
            >
              {savingEdit ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </div>
      ) : (
        current && (
          <SummaryTabBody
            row={current}
            busy={busyProfile === current.profile}
            onRetry={() => handleRegen(current.profile)}
          />
        )
      )}
    </section>
  );
}

/** Тело текущего таба — рендер по статусу варианта саммари. */
function SummaryTabBody({
  row,
  busy,
  onRetry,
}: {
  row: SummaryRow;
  busy: boolean;
  onRetry: () => void;
}) {
  if (row.status === "pending" || row.status === "running") {
    return (
      <p className="muted">
        Конспект этого типа генерируется… Страница обновится автоматически.
      </p>
    );
  }
  if (row.status === "failed") {
    return (
      <div>
        <p className="error summary-empty-note">
          Не удалось сгенерировать конспект этого типа.
        </p>
        <button
          type="button"
          className="page-button"
          onClick={onRetry}
          disabled={busy}
        >
          {busy ? "Ставим в очередь…" : "Повторить"}
        </button>
      </div>
    );
  }
  if (!row.summary?.trim()) {
    return <p className="muted">Конспект пуст.</p>;
  }
  return <SummaryMarkdown text={row.summary} />;
}

/* Минималистичный markdown-renderer для AI-сгенерированного summary.
 * Поддерживаются:
 *   - `## Заголовок` → <h4>
 *   - `- пункт` → буллет
 *   - `**bold**` → <strong>
 * Полноценный markdown (react-markdown) — overkill для одного use case
 * и +50 KB к bundle. */
function SummaryMarkdown({ text }: { text: string }) {
  const lines = text.split("\n");
  type Block =
    | { kind: "h"; text: string }
    | { kind: "li"; text: string }
    | { kind: "p"; text: string }
    | { kind: "gap" };

  const blocks: Block[] = lines.map((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) return { kind: "gap" };
    if (line.startsWith("## ")) return { kind: "h", text: line.slice(3) };
    if (line.startsWith("# ")) return { kind: "h", text: line.slice(2) };
    if (line.startsWith("- ")) return { kind: "li", text: line.slice(2) };
    if (line.startsWith("* ")) return { kind: "li", text: line.slice(2) };
    return { kind: "p", text: line };
  });

  // Группируем последовательные li в один ul.
  const out: JSX.Element[] = [];
  let buf: string[] = [];
  const flushList = () => {
    if (buf.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`}>
        {buf.map((b, i) => (
          <li key={i}>{renderInline(b)}</li>
        ))}
      </ul>,
    );
    buf = [];
  };

  blocks.forEach((b, i) => {
    if (b.kind === "li") {
      buf.push(b.text);
      return;
    }
    flushList();
    if (b.kind === "h") {
      out.push(<h4 key={`h-${i}`}>{renderInline(b.text)}</h4>);
    } else if (b.kind === "p") {
      out.push(<p key={`p-${i}`}>{renderInline(b.text)}</p>);
    } else if (b.kind === "gap") {
      // visual gap — пустой <p> игнорим, но между h/ul margin создадим
      // через CSS на сами элементы. Не пушим ничего.
    }
  });
  flushList();

  return <div className="summary-body">{out}</div>;
}

function renderInline(text: string): ReactNode[] {
  // **bold** → <strong>. Простой split по ** парам.
  const parts: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    parts.push(<strong key={`b-${m.index}`}>{m[1]}</strong>);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

/**
 * AccessList — read-only список acl_users (кто видит запись). Manager
 * редактирует этот список через ManagerPanel ниже (с autocomplete'ом по
 * Authentik users + ручным MXID вводом).
 *
 * Для звонка acl_users изначально = participants на момент finalize.
 * Для upload'а acl_users изначально = [владелец].
 * После — manager может расшарить добавив других пользователей.
 *
 * Если acl_users пуст — секцию не рендерим, чтобы не показывать пустую плашку.
 */
function AccessList({ rec }: { rec: Recording }) {
  const users = rec.acl_users ?? [];
  if (users.length === 0) return null;
  return (
    <section className="access-list">
      <h3>Доступ ({users.length})</h3>
      <ul className="access-list-items">
        {users.map((u) => (
          <li key={u} className="access-list-user">
            {u}
          </li>
        ))}
      </ul>
    </section>
  );
}

function RecordingMeta({ rec }: { rec: Recording }) {
  const startedAt = new Date(rec.started_at);
  const dateStr =
    startedAt.toLocaleDateString() +
    " " +
    startedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <p className="muted recording-meta">
      <span>{dateStr}</span>
      <span>·</span>
      <span>комната: {rec.room_display_name ?? rec.room_id}</span>
      <span>·</span>
      <span>владелец: {rec.started_by || "—"}</span>
    </p>
  );
}

/**
 * RecordingTitle — заголовок записи (H1). Для менеджера — с inline-
 * переименованием (✎). Название записи переехало сюда из «Управления»: там
 * теперь только доступ и удаление. Viewer'у редактирование недоступно.
 */
function RecordingTitle({
  title,
  canEdit,
  onRename,
}: {
  title: string;
  canEdit: boolean;
  onRename: (title: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setDraft(title);
  }, [title]);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onRename(draft.trim());
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit || !editing) {
    return (
      <div className="recording-title-row">
        <h1>{title}</h1>
        {canEdit && (
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setDraft(title);
              setErr(null);
              setEditing(true);
            }}
          >
            ✎ Переименовать
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="recording-title-edit">
      <input
        type="text"
        className="filter-input title-edit-input"
        value={draft}
        maxLength={500}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !saving && draft.trim() !== "") void save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <div className="manager-row row-end row-mt">
        <button
          type="button"
          className="link-button"
          disabled={saving}
          onClick={() => setEditing(false)}
        >
          Отмена
        </button>
        <button
          type="button"
          className="page-button"
          disabled={saving || draft.trim() === ""}
          onClick={save}
        >
          {saving ? "Сохранение…" : "Сохранить"}
        </button>
      </div>
      {err && <p className="error">{err}</p>}
    </div>
  );
}

/**
 * OwnerPanel показывает владельца всем управляющим записью. Admin может
 * переназначить владельца записи (started_by). Тот же
 * autocomplete по Authentik users, что и в SharePanel, но одиночный выбор:
 * клик по пользователю сразу назначает его владельцем (PATCH started_by).
 */
function OwnerPanel({
  recording,
  canReassign,
  onSave,
}: {
  recording: Recording;
  canReassign: boolean;
  onSave: (mxid: string) => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AutocompleteUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (query.trim().length === 0) {
      setSuggestions([]);
      return;
    }
    setSearching(true);
    const h = setTimeout(() => {
      listUsers(query, 20)
        .then(setSuggestions)
        .finally(() => setSearching(false));
    }, 200);
    return () => clearTimeout(h);
  }, [query]);

  const trimmed = query.trim();
  const showManualHint =
    MXID_RE.test(trimmed) && !suggestions.some((u) => u.mxid === trimmed);

  const assign = async (mxid: string) => {
    if (saving) return;
    setSaving(true);
    setErr(null);
    try {
      await onSave(mxid);
      setQuery("");
      setSuggestions([]);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manager-field share-panel">
      <span className="manager-label">Владелец записи</span>
      <p className="muted panel-note">
        {recording.started_by || "—"}
      </p>
      {canReassign && <div className="share-add">
        <input
          type="text"
          className="filter-input input-full"
          aria-label="Назначить владельца записи"
          placeholder="Назначить владельцем (имя или @user:nan.ru)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={saving}
        />
        {(suggestions.length > 0 || showManualHint || searching) && (
          <ul className="share-suggestions">
            {suggestions.map((u) => (
              <li key={u.mxid}>
                <button
                  type="button"
                  className="share-suggestion-row"
                  disabled={saving}
                  onClick={() => assign(u.mxid)}
                >
                  <span className="share-suggestion-name">
                    {u.display_name || u.mxid}
                  </span>
                  {u.display_name && (
                    <span className="share-suggestion-mxid muted">{u.mxid}</span>
                  )}
                </button>
              </li>
            ))}
            {showManualHint && (
              <li>
                <button
                  type="button"
                  className="share-suggestion-row"
                  disabled={saving}
                  onClick={() => assign(trimmed)}
                >
                  <span>+ Назначить {trimmed}</span>
                </button>
              </li>
            )}
            {searching && suggestions.length === 0 && !showManualHint && (
              <li className="muted share-suggestion-empty">Поиск…</li>
            )}
          </ul>
        )}
      </div>}
      {saving && <p className="action-status-row"><ActionStatus>Назначаем владельца…</ActionStatus></p>}
      {err && (
        <p className="error field-error">
          {err}
        </p>
      )}
    </div>
  );
}

/**
 * ManagerPanel — «Управление»: доступ к записи (шаринг) + удаление. Для admin
 * дополнительно — переназначение владельца (started_by). Название правится
 * inline в H1, текст конспекта — по-вкладочно. Панель рендерится только тем,
 * кто может управлять записью (canManage).
 */
function ManagerPanel({
  recording,
  isAdmin,
  canConfidential,
  onChanged,
  onSave,
  onDelete,
  deleting,
}: {
  recording: Recording;
  isAdmin: boolean;
  canConfidential: boolean;
  onChanged: (recording: Recording) => void;
  onSave: (payload: PatchPayload) => Promise<void>;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <section className="manager-panel">
      <h3>Управление</h3>

      <OwnerPanel
        recording={recording}
        canReassign={isAdmin}
        onSave={async (mxid) => {
          await onSave({ started_by: mxid });
        }}
      />
      <VisibilityPanel rec={recording} canManage canConfidential={canConfidential} onChanged={onChanged} />
      <SharePanel
        recording={recording}
        onSave={async (aclUsers) => {
          await onSave({ acl_users: aclUsers });
        }}
      />
      <TagPanel rec={recording} canManage onChanged={onChanged} />

      <div className="manager-field manager-danger">
        <button type="button" className="danger-button" disabled={deleting} onClick={onDelete}>
          Удалить запись
        </button>
        {deleting && <p className="action-status-row"><ActionStatus>Удаляем запись…</ActionStatus></p>}
      </div>
    </section>
  );
}

/**
 * SharePanel — manager редактирует acl_users записи.
 *
 * Состоит из:
 *   - чипы с текущими acl_users (× удаляет),
 *   - поле «Добавить пользователя» с autocomplete по Authentik users,
 *   - fallback на «+ Добавить @user:host» когда в подсказке нужного нет
 *     (валидируем формат MXID на лету тем же regex что и бэк).
 *   - кнопка «Сохранить» PATCH'ит полный acl_users массив.
 *
 * Особенности:
 *   - Локальный draft (chips) — изменения накапливаются до Save;
 *   - При запросе /v1/users пустой массив (Authentik API не сконфигурирован)
 *     означает manual-only mode — autocomplete просто не появляется,
 *     manager печатает MXID руками.
 */
function SharePanel({
  recording,
  onSave,
}: {
  recording: Recording;
  onSave: (aclUsers: string[]) => Promise<void>;
}) {
  // Draft — текущее редактируемое состояние списка. При маунте/смене записи
  // синхронизируемся с recording.acl_users.
  const [draft, setDraft] = useState<string[]>(recording.acl_users ?? []);
  useEffect(() => {
    setDraft(recording.acl_users ?? []);
  }, [recording.id, recording.acl_users]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const initial = recording.acl_users ?? [];
  const dirty = draft.length !== initial.length || draft.some((user, index) => user !== initial[index]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draft);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manager-field share-panel">
      <label className="manager-label">Персональный доступ к записи</label>
      <p className="muted panel-note">Суперменеджер, получивший доступ, сможет редактировать запись и делиться ею.</p>

      {draft.length === 0 && <p className="muted panel-note">Никто не добавлен персонально. Другие пользователи могут получать доступ через режим записи и метки.</p>}
      <UserPicker users={draft} onChange={setDraft} disabled={saving} />

      <div className="manager-row row-end">
        <button
          type="button"
          className="page-button"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? "Сохранение…" : "Сохранить доступ"}
        </button>
      </div>
      {saving && <p className="action-status-row"><ActionStatus>Сохраняем персональный доступ…</ActionStatus></p>}
      {saveError && (
        <p className="error field-error">
          {saveError}
        </p>
      )}
    </div>
  );
}

function DownloadLinks({
  videoURL,
  videoLabel,
  txtURL,
  jsonURL,
}: {
  videoURL?: string;
  /** Подпись для ссылки на медиа. По умолчанию "Скачать видео (.webm)". */
  videoLabel?: string;
  txtURL?: string;
  jsonURL?: string;
}) {
  // ?download=1 → бэк выставляет Content-Disposition: attachment с осмысленным
  // именем "<date>_<title-или-room>.<ext>". Без параметра — inline (нужно
  // для HTML5 <video> плеера на этой же странице).
  //
  // На <a> ставим пустой `download` (без значения!) — он сигналит браузеру
  // "скачать", но не подменяет имя из Content-Disposition (передача value
  // override'нула бы наш filename*). У нас same-origin, поэтому download
  // работает.
  const dl = (href: string): string => href + (href.includes("?") ? "&" : "?") + "download=1";
  const links: { label: string; href: string }[] = [];
  if (videoURL)
    links.push({
      label: videoLabel || "Скачать видео (.webm)",
      href: dl(videoURL),
    });
  if (txtURL) links.push({ label: "Транскрипт (.txt)", href: dl(txtURL) });
  if (jsonURL) links.push({ label: "Транскрипт (.json)", href: dl(jsonURL) });
  if (links.length === 0) return null;
  return (
    <div className="download-links">
      {links.map((l) => (
        <a key={l.href} href={l.href} download="" rel="noreferrer">
          {l.label}
        </a>
      ))}
    </div>
  );
}

function TranscriptPanel({
  segments,
  currentTime,
  onClick,
}: {
  segments: { start: number; end: number; text: string; speaker?: string }[];
  currentTime: number;
  onClick: (start: number) => void;
}) {
  // Индекс активного сегмента — для auto-scroll. Бинарным поиском (segments
  // отсортированы по start), чтобы O(log N) на каждый видео-tick а не O(N).
  const activeIdx = useMemo(() => findActiveSegment(segments, currentTime), [
    segments,
    currentTime,
  ]);

  // Speaker remap: pyannote выдаёт internal IDs типа "SPEAKER_00", "SPEAKER_06"
  // — нумерация с дырками, не связана ни с количеством, ни с порядком
  // появления. Строим map raw_label → "Speaker N" по первому появлению, чтобы
  // пользователь видел "Speaker 1, Speaker 2..." последовательно. Файлы в S3
  // (transcript.txt/.json) оставлены с raw-метками — это исходные данные,
  // ремап делаем только в UI на рендере.
  const speakerMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const seg of segments) {
      const raw = seg.speaker;
      if (raw && !map.has(raw)) {
        map.set(raw, `Speaker ${map.size + 1}`);
      }
    }
    return map;
  }, [segments]);

  // Ref на контейнер списка и активный элемент для управления auto-scroll.
  const listRef = useRef<HTMLOListElement | null>(null);
  const activeRef = useRef<HTMLLIElement | null>(null);

  // Auto-follow — включён пока пользователь не скроллил руками. Если юзер
  // вручную сдвинул скролл — выключаем, чтобы не вырывать его обратно.
  // Повторно включается кликом по сегменту (handleSegmentClick).
  const [autoFollow, setAutoFollow] = useState(true);

  useEffect(() => {
    if (!autoFollow || activeIdx < 0) return;
    const el = activeRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIdx, autoFollow]);

  // Если контейнер скроллится не из-за нашего scrollIntoView (т.е. wheel/touch
  // от пользователя) — отключаем auto-follow. Heuristic: scrollIntoView с smooth
  // длится <500ms; ручной wheel поднимает scroll event и не считается из нашего
  // программного.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let programmaticScrollUntil = 0;
    const onScroll = () => {
      if (Date.now() < programmaticScrollUntil) return;
      setAutoFollow(false);
    };
    // Запускаем программную метку перед каждым scrollIntoView (см. эффект выше).
    // Простой workaround: считаем scroll'ы первые 400ms после activeIdx change
    // программными.
    programmaticScrollUntil = Date.now() + 400;
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeIdx]);

  return (
    <div className="transcript">
      <div className="transcript-header">
        <h3>Транскрипт</h3>
        {!autoFollow && (
          <button
            type="button"
            className="link-button"
            onClick={() => setAutoFollow(true)}
          >
            ↓ Следить
          </button>
        )}
      </div>
      <ol className="transcript-list" ref={listRef}>
        {segments.map((seg, idx) => {
          const active = idx === activeIdx;
          return (
            <li
              key={idx}
              ref={active ? activeRef : null}
              className={"transcript-row" + (active ? " transcript-row--active" : "")}
              onClick={() => {
                onClick(seg.start);
                setAutoFollow(true);
              }}
            >
              <span className="transcript-time">{formatTime(seg.start)}</span>
              {seg.speaker && (
                <span className="transcript-speaker">
                  {speakerMap.get(seg.speaker) ?? seg.speaker}
                </span>
              )}
              <span className="transcript-text">{seg.text.trim()}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Бинарный поиск: индекс сегмента где start ≤ t < end, или -1 если нет совпадения. */
function findActiveSegment(
  segments: { start: number; end: number }[],
  t: number,
): number {
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const seg = segments[mid];
    if (t < seg.start) hi = mid - 1;
    else if (t >= seg.end) lo = mid + 1;
    else return mid;
  }
  return -1;
}

function formatTime(sec: number): string {
  const totalSec = Math.floor(sec);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
